/**
 * Operator portal rework PR 5 — "view as operator" in the portal tab.
 *
 * A Paelode admin opens `/portal/view-as?code=…` from web-admin. ViewAsStartPage exchanges the
 * one-time code for a session token; this module keeps that session for the tab
 * (sessionStorage, so it never outlives the tab or leaks into another one) and makes every API
 * call carry the token instead of an Amplify session (web-shared `setAccessTokenOverride`).
 *
 * The server enforces read-only; nothing here is a security boundary. The tab only needs to know
 * whether it is viewing, whose view it is, and when it has ended: at expiry, from the banner's
 * End button, or when the API answers 401 (an admin ended it from web-admin).
 */

import { useSyncExternalStore } from 'react';
import { authedFetch, setAccessTokenOverride, type WebApiOpts } from '@lodex-pro/web-shared';

export interface ViewAsSession {
  readonly token: string;
  readonly expiresAt: string;
  readonly operatorName: string;
  readonly targetName: string | null;
  readonly role: string | null;
}

export interface ViewAsState {
  /** The live session, or null. */
  readonly session: ViewAsSession | null;
  /** True once a session has ended in this tab (shows the "ended" page, not sign-in). */
  readonly ended: boolean;
}

const STORAGE_KEY = 'lodex.portal.viewAs';

let state: ViewAsState = { session: null, ended: false };
const listeners = new Set<() => void>();
let expiryTimer: ReturnType<typeof setTimeout> | null = null;

function emit(next: ViewAsState): void {
  state = next;
  for (const l of listeners) l();
}

function readStorage(): ViewAsSession | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<ViewAsSession>;
    if (typeof parsed.token !== 'string' || typeof parsed.expiresAt !== 'string') return null;
    return parsed as ViewAsSession;
  } catch {
    return null;
  }
}

function writeStorage(session: ViewAsSession | null): void {
  try {
    if (session === null) window.sessionStorage.removeItem(STORAGE_KEY);
    else window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Storage blocked: the session lives for this page load only.
  }
}

/** Drop the session in this tab; the token stops being sent. */
export function markViewAsEnded(): void {
  if (expiryTimer !== null) clearTimeout(expiryTimer);
  expiryTimer = null;
  writeStorage(null);
  setAccessTokenOverride(null);
  emit({ session: null, ended: state.session !== null || state.ended });
}

function activate(session: ViewAsSession): void {
  const msLeft = Date.parse(session.expiresAt) - Date.now();
  if (!(msLeft > 0)) {
    writeStorage(null);
    emit({ session: null, ended: true });
    return;
  }
  setAccessTokenOverride(session.token, { onUnauthorized: markViewAsEnded });
  if (expiryTimer !== null) clearTimeout(expiryTimer);
  expiryTimer = setTimeout(markViewAsEnded, msLeft);
  emit({ session, ended: false });
}

/** Start viewing with a freshly exchanged session. */
export function beginViewAs(session: ViewAsSession): void {
  writeStorage(session);
  activate(session);
}

/** On page load: resume a session this tab already holds (a reload keeps the view). */
export function resumeViewAs(): void {
  const stored = readStorage();
  if (stored !== null) activate(stored);
}

/** End the session on the server (best-effort), then in this tab. */
export async function endViewAs(opts: WebApiOpts): Promise<void> {
  try {
    await authedFetch(opts, '/v1/view-as/end', { method: 'POST', body: '{}' });
  } catch {
    // The tab ends regardless; an unended session still expires on the server.
  }
  markViewAsEnded();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useViewAs(): ViewAsState {
  return useSyncExternalStore(subscribe, () => state);
}

/** Test-only: reset module state. */
export function __resetViewAsForTesting(): void {
  if (expiryTimer !== null) clearTimeout(expiryTimer);
  expiryTimer = null;
  setAccessTokenOverride(null);
  state = { session: null, ended: false };
  writeStorage(null);
}
